import { yToMinutes, computeDrop } from '../helpers/dragGeometry';
import { isBlockingEvent } from '../helpers/blockingEvents';

const MOVE_THRESHOLD_PX = 5;          // mouse: move beyond this = drag (otherwise a click)
const TOUCH_HOLD_MS = 600;            // touch: press-and-hold this long to pick up a lesson
const TOUCH_SCROLL_THRESHOLD_PX = 10; // touch: moving beyond this before pickup = scroll, not drag
const SNAP_THRESHOLD_MIN = 20;        // magnetic distance to a free slot
const SUPPRESS_MOUSE_AFTER_TOUCH_MS = 700; // ignore the compatibility mouse events a tap emits

export const DragRescheduleMixin = {
    data() {
        return {
            // Active drag session (mouse after the move threshold, or touch after the long-press).
            drag: null,
            // Touch gesture awaiting the long-press: { event, startX, startY, lastX, lastY, holdTimer }.
            touchPending: null,
        };
    },

    methods: {
        // Managers can drag any non-recurring future event; students only their own
        // practice lessons (into free slots).
        isDraggableEvent(event) {
            if (isBlockingEvent(event)) return false;
            // Picking holiday days is a modal state: every click in the grid selects a
            // day, so a drag started here would reschedule a lesson the user only meant
            // to tap. (Undefined outside the week view, which has no select mode.)
            if (this.vacationSelectMode) return false;
            if (this.dragMode === 'off' || this.action === 'show') return false;
            if (event._start_time < this.now) return false;
            // Moving a single occurrence of a recurring series is ambiguous — skip it.
            if (event.is_recurring) return false;
            if (this.dragMode === 'student') {
                // Students only drag their OWN lesson (see `is_mine` from the API).
                return event.type === 'practice-lesson' && !!event.is_mine;
            }
            return true;
        },

        // ---- Mouse / pen (Pointer Events) ------------------------------------------------

        onEventPointerDown(event, domEvent) {
            if (domEvent.pointerType === 'touch') return; // touch handled by onEventTouchStart
            // A touch gesture is already in progress (some setups, e.g. desktop touch-emulation,
            // fire both touch and mouse events) — don't start a second, parallel mouse drag.
            if (this.touchPending || this.drag) return;
            // Ignore the synthetic mouse events a touch emits shortly after a tap.
            if (Date.now() - (this.lastTouchAt || 0) < SUPPRESS_MOUSE_AFTER_TOUCH_MS) return;
            if (!this.isDraggableEvent(event)) return; // fall through to normal @click
            if (domEvent.button != null && domEvent.button !== 0) return;

            // Stop the browser from starting a native text selection on a mouse drag.
            domEvent.preventDefault();

            this.drag = {
                event,
                pointerId: domEvent.pointerId,
                startX: domEvent.clientX,
                startY: domEvent.clientY,
                grabOffsetY: this.grabOffsetY(domEvent.currentTarget, domEvent.clientY),
                started: false,
                targetDate: null,
                targetStartMin: null,
                valid: false,
            };

            window.addEventListener('pointermove', this.onPointerMove);
            window.addEventListener('pointerup', this.onPointerUp);
            window.addEventListener('pointercancel', this.onPointerUp);
        },

        onPointerMove(domEvent) {
            const d = this.drag;
            if (!d) return;
            if (domEvent.pointerId !== d.pointerId) return;

            if (!d.started) {
                if (Math.hypot(domEvent.clientX - d.startX, domEvent.clientY - d.startY) < MOVE_THRESHOLD_PX) return;
                d.started = true;
                document.body.classList.add('timetable-dragging');
            }
            this.moveDragTo(domEvent.clientX, domEvent.clientY);
        },

        onPointerUp(domEvent) {
            if (this.drag && domEvent && domEvent.pointerId !== this.drag.pointerId) return;
            const d = this.drag;
            this.teardownDrag();
            if (!d) return;

            if (!d.started) {
                this.clickEvent(d.event, d.event._start_time); // tap/click => open modal
                return;
            }
            this.commitDrop(d);
        },

        // ---- Touch (long-press to drag, swipe to scroll) ---------------------------------

        onEventTouchStart(event, domEvent) {
            this.lastTouchAt = Date.now();
            if (!this.isDraggableEvent(event)) return; // let normal tap/scroll happen
            if (domEvent.touches.length !== 1) return; // ignore multi-touch (pinch/zoom)

            const t = domEvent.touches[0];
            this.touchPending = {
                event,
                startX: t.clientX,
                startY: t.clientY,
                lastX: t.clientX,
                lastY: t.clientY,
                grabOffsetY: this.grabOffsetY(domEvent.currentTarget, t.clientY),
                holdTimer: null,
            };

            // Pick the lesson up only after a press-and-hold; a swipe before then scrolls.
            this.touchPending.holdTimer = setTimeout(() => {
                const p = this.touchPending;
                if (!p) return;
                this.drag = {
                    event: p.event,
                    grabOffsetY: p.grabOffsetY,
                    started: true,
                    targetDate: null,
                    targetStartMin: null,
                    valid: false,
                };
                document.body.classList.add('timetable-dragging');
                if (window.navigator && navigator.vibrate) navigator.vibrate(15);
                this.moveDragTo(p.lastX, p.lastY);
            }, TOUCH_HOLD_MS);

            // touchmove must be non-passive so we can preventDefault scrolling once picked up.
            window.addEventListener('touchmove', this.onTouchMove, { passive: false });
            window.addEventListener('touchend', this.onTouchEnd);
            window.addEventListener('touchcancel', this.onTouchEnd);
        },

        onTouchMove(domEvent) {
            const p = this.touchPending;
            if (!p) return;
            const t = domEvent.touches[0];
            if (!t) return;
            this.lastTouchAt = Date.now(); // keep the mouse-suppression window fresh during a long drag
            p.lastX = t.clientX;
            p.lastY = t.clientY;

            if (this.drag && this.drag.started) {
                // Picked up — suppress scrolling and move the placeholder with the finger.
                domEvent.preventDefault();
                this.moveDragTo(t.clientX, t.clientY);
                return;
            }

            // Not picked up yet: movement means the user is scrolling — let the page scroll.
            if (Math.hypot(t.clientX - p.startX, t.clientY - p.startY) > TOUCH_SCROLL_THRESHOLD_PX) {
                this.cancelTouchPending();
            }
        },

        onTouchEnd(domEvent) {
            // Refresh so the compatibility mouse events fired ~300ms after touchend are ignored,
            // even after a long drag.
            this.lastTouchAt = Date.now();
            const p = this.touchPending;
            const d = this.drag;
            const picked = !!(d && d.started);
            const cancelled = domEvent && domEvent.type === 'touchcancel';

            this.cancelTouchPending();
            this.teardownDrag();

            if (cancelled || !p) return;

            const moved = Math.hypot(p.lastX - p.startX, p.lastY - p.startY);

            // A finger that never moved is a tap, however long it rested: a slow tap
            // on a phone easily outlasts the long-press. Treating it as a drop did
            // nothing where no free slot was near (lessons after work hours), so the
            // lesson never opened and its edit/delete buttons were out of reach.
            if (picked && moved > TOUCH_SCROLL_THRESHOLD_PX) {
                this.commitDrop(d);
                return;
            }
            if (moved <= TOUCH_SCROLL_THRESHOLD_PX) this.clickEvent(p.event, p.event._start_time);
        },

        cancelTouchPending() {
            const p = this.touchPending;
            if (p && p.holdTimer) clearTimeout(p.holdTimer);
            window.removeEventListener('touchmove', this.onTouchMove);
            window.removeEventListener('touchend', this.onTouchEnd);
            window.removeEventListener('touchcancel', this.onTouchEnd);
            this.touchPending = null;
        },

        // ---- Shared drag core ------------------------------------------------------------

        moveDragTo(clientX, clientY) {
            const d = this.drag;
            if (!d) return;

            // Which day column is the pointer/finger over?
            const colEl = document.elementsFromPoint(clientX, clientY)
                .find(el => el.classList && el.classList.contains('timetable-column'));
            if (!colEl) return;

            const contentEl = colEl.querySelector('.timetable-column-content');
            const contentRect = contentEl.getBoundingClientRect();
            const di = parseInt(colEl.getAttribute('data-col-index'), 10);
            const targetDate = this.dates[di];
            if (targetDate == null) return;

            // The block keeps its place under the finger: grabbed by the middle, its
            // top stays half a block above the pointer instead of jumping down to it.
            const yInContent = clientY - contentRect.top - (d.grabOffsetY || 0);
            const proposedStartMin = yToMinutes(yInContent, this.rowHeight, this.calendarStartHour);

            // Free-slots-only when a student is dragging, or when the admin setting restricts
            // managers to drop practice lessons onto free instructor slots only.
            const freeSlotsOnly = this.dragMode === 'student'
                || (this.restrictPracticeToFreeSlots && d.event.type === 'practice-lesson');

            const result = computeDrop({
                mode: freeSlotsOnly ? 'student' : 'manager',
                proposedStartMin,
                // Magnetic snap to free slots is an instructor-availability concept, so it
                // only applies to practice lessons. Other event types snap to the time grid.
                freeSlots: d.event.type === 'practice-lesson'
                    ? this.freeSlotsForDate(targetDate, d.event.id)
                    : [],
                durationMinutes: d.event._duration_in_minutes,
                snapMinutes: this.snapMinutes,
                thresholdMin: SNAP_THRESHOLD_MIN,
                startHour: this.calendarStartHour,
                // The grid draws a row for calendarEndHour itself, so it ends an hour
                // later. Clamping to calendarEndHour pulled late lessons up: a 90-minute
                // lesson could not start after 19:30 on a grid that runs to 22:00.
                endHour: this.calendarEndHour + 1,
            });

            // A lesson may never be dropped into the past — the target slot must start now
            // or later (mirrors the rule that past lessons can't be picked up in the first place).
            const targetStart = moment(targetDate).startOf('day').add(result.startMin, 'minutes');
            if (targetStart.isBefore(moment())) {
                result.valid = false;
            }

            d.targetDate = targetDate;
            d.targetStartMin = result.startMin;
            d.valid = result.valid;

            this.updatePlaceholder(contentEl, result, d.event._duration_in_minutes);
            this.updateGhostAt(clientX, clientY, result, d.event._duration_in_minutes);
        },

        // Distance from the top of the grabbed block to the pointer, so the drag can
        // move the block by its top edge rather than by the point it was grabbed at.
        grabOffsetY(el, clientY) {
            if (!el || !el.getBoundingClientRect) return 0;
            return clientY - el.getBoundingClientRect().top;
        },

        commitDrop(d) {
            if (d.targetDate == null) return;
            if (!d.valid) {
                // Snap back, but say why: a silent no-op read as "moving is broken",
                // most of all for lessons outside work hours, where no free slot is near.
                if (this.$notify) {
                    const texts = this.texts || {};
                    // Students have no edit button: they can only move into a free slot.
                    const text = this.dragMode === 'student'
                        ? (texts.drop_not_allowed_student
                            || 'Lecția poate fi mutată doar într-un loc liber al instructorului.')
                        : (texts.drop_not_allowed
                            || 'Lecția nu poate fi mutată aici. Pentru altă oră, deschideți lecția și apăsați pe editare.');
                    this.$notify({ group: 'app', type: 'warning', text });
                }
                return;
            }

            const newStart = moment(d.targetDate).startOf('day').add(d.targetStartMin, 'minutes');
            const newEnd = newStart.clone().add(d.event._duration_in_minutes, 'minutes');

            if (newStart.isBefore(moment())) return; // never drop into the past
            if (newStart.isSame(d.event._start_time)) return; // nothing changed

            const clone = { ...d.event };
            clone.start_time = newStart.format();
            clone.start_date = newStart.format();
            clone.end_time = newEnd.format();

            this.eventBus.$emit('reschedule-event', clone, () => {
                // On error the toast is shown by EventAPI; the original event stays put.
            });
        },

        // A box drawn in the target column showing exactly where the event will land.
        updatePlaceholder(contentEl, result, durationMinutes) {
            if (!this.drag) return;
            let el = this.drag.placeholder;
            if (!el) {
                el = document.createElement('div');
                el.className = 'timetable-drop-placeholder';
                this.drag.placeholder = el;
            }
            if (el.parentNode !== contentEl) {
                contentEl.appendChild(el); // follow the pointer across day columns
            }
            el.style.top = this.rowHeight * (result.startMin / 60 - this.calendarStartHour) + 'px';
            el.style.height = this.rowHeight * (durationMinutes / 60) + 'px';
            el.classList.toggle('invalid', !result.valid);
        },

        updateGhostAt(clientX, clientY, result, durationMinutes) {
            if (!this.drag) return;
            if (!this.drag.ghost) {
                const el = document.createElement('div');
                el.className = 'timetable-drag-ghost';
                document.body.appendChild(el);
                this.drag.ghost = el;
            }
            const startMin = result.startMin;
            const endMin = startMin + durationMinutes;
            const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
            this.drag.ghost.textContent = `${fmt(startMin)} – ${fmt(endMin)}`;
            this.drag.ghost.classList.toggle('invalid', !result.valid);
            this.drag.ghost.style.left = clientX + 12 + 'px';
            this.drag.ghost.style.top = clientY - 28 + 'px';
        },

        teardownDrag() {
            window.removeEventListener('pointermove', this.onPointerMove);
            window.removeEventListener('pointerup', this.onPointerUp);
            window.removeEventListener('pointercancel', this.onPointerUp);
            document.body.classList.remove('timetable-dragging');
            this.drag = null;
            // Defensively sweep every drag visual from the DOM, not just the current session's
            // refs — covers a second input path (e.g. desktop touch-emulation firing both touch
            // and mouse events) leaving an orphaned placeholder/ghost behind.
            document.querySelectorAll('.timetable-drag-ghost, .timetable-drop-placeholder')
                .forEach((el) => el.remove());
        },

        onMaybeClick(event, date, domEvent) {
            // Vacation blocks never open the lesson show/edit modal. They used to be
            // fully inert, which left the small button in their corner as the only way
            // to reach the holiday — and a lesson at the first visible hour paints
            // straight over that corner, making the holiday uneditable from the grid.
            // The whole backdrop is the target now; the button stays as the visual cue.
            if (isBlockingEvent(event)) {
                if (this.canManageVacations) this.manageVacation(event);
                return;
            }
            // Draggable events open via onPointerUp / onTouchEnd; only non-draggable events
            // use the plain click to open.
            if (!this.isDraggableEvent(event)) {
                this.clickEvent(event, date);
            }
        },

        // Stop the native long-press menu (context menu / text-selection callout) from
        // hijacking the press-and-hold gesture on draggable lessons.
        onEventContextMenu(event, domEvent) {
            if (this.isDraggableEvent(event)) domEvent.preventDefault();
        },
    },

    beforeDestroy() {
        this.cancelTouchPending();
        this.teardownDrag();
    },
};
